import { useState, useEffect, useRef, useCallback } from "react";
import { t } from "i18n";

const useInfiniteScroll = ({
  fetchFn,
  search = "",
  pageSize = 20,
  extraParams = {},
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [error, setError] = useState(null);
  // Bumped by refresh() so a retry refetches even when already on page 1.
  const [refreshTick, setRefreshTick] = useState(0);

  const observer = useRef();
  const searchRef = useRef(search);

  // Serialize extra params (e.g. filters) so we can reset/refetch on change
  const extraKey = JSON.stringify(extraParams);
  const extraRef = useRef(extraKey);
  const extraParamsRef = useRef(extraParams);
  extraParamsRef.current = extraParams;

  // Reset when search or filters change
  useEffect(() => {
    if (searchRef.current !== search || extraRef.current !== extraKey) {
      searchRef.current = search;
      extraRef.current = extraKey;
      setItems([]);
      setPage(1);
      setHasMore(true);
    }
  }, [search, extraKey]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetchFn({
          page,
          pageSize,
          search,
          ...extraParamsRef.current,
        });

        const newItems = response.data?.results || response.data || [];
        const total =
          response.data?.total_count ?? response.data?.count ?? newItems.length;

        if (page === 1) {
          setItems(newItems);
        } else {
          setItems((prev) => [...prev, ...newItems]);
        }

        setTotalCount(total);
        setHasMore(newItems.length === pageSize);
        setError(null);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(error?.message || t("utils.infiniteScroll.loadFailed"));
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fetchFn, page, pageSize, search, extraKey, refreshTick]);

  // Intersection observer for infinite scroll
  const lastItemRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prev) => prev + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  // Refresh function to reset and refetch
  const refresh = useCallback(() => {
    setItems([]);
    setPage(1);
    setHasMore(true);
    setError(null);
    setRefreshTick((tick) => tick + 1);
  }, []);

  return {
    items,
    loading,
    hasMore,
    totalCount,
    error,
    lastItemRef,
    refresh,
  };
};

export default useInfiniteScroll;
